<template>
  <div
    class="one-page-navigation"
    :class="{'one-page-navigation--sticky': isSticky}"
    v-if="!isHidden"
  >
    <div class="one-page-navigation__wrapper">
      <div class="one-page-navigation__container">
        <nav class="one-page-navigation__items">
          <a
            class="one-page-navigation__item"
            :class="{
              'one-page-navigation__item--active': isNavItemActive(item),
              'one-page-navigation__item--disabled': item.disabled,
            }"
            v-for="item in navItems"
            :key="item.anchor"
            :href="`#${item.anchor}`"
            @click="onItemClick($event, item)"
            :ref="isNavItemActive(item) ? 'activeNavElement' : null"
          >
            {{ item.title }}
          </a>
        </nav>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
  import {Vue, Options, Prop, Watch} from 'vue-property-decorator';
  import * as scrollToElement from 'scroll-to-element';
  import {throttle} from '../../utilities/js/throttle/throttle';
  import pigeon from '../../utilities/js/pigeon/pigeon';
  import {breakpoints} from '../../variables/variables';
  import {documentHelper} from '../../utilities/js/documentHelper/documentHelper';


  // Usage:
  // <a data-nav-anchor="anchor" data-nav-title="MyTitle" />
  // <section data-nav-anchor="anchor" data-nav-title="MyTitle" data-nav-disabled> ... </section>;
  //
  // anchor mode true: uses anchors (without height) to determine current item
  // anchor mode false: uses containers (with height) to determine current item

  type NavItem = {
    anchor: string,
    title: string,
    disabled: boolean,
  }

  @Options({
    name: 'one-page-navigation'
  })
  export default class OnePageNavigation extends Vue {
    @Prop({default: 0, type: Number})
    readonly offsetBasis: number;

    @Prop({default: true, type: Boolean})
    readonly hideOnMobile: boolean;

    @Prop({default: true, type: Boolean})
    readonly modifyUrl: boolean;

    @Prop({default: false, type: Boolean})
    readonly anchorMode: boolean;

    elements: HTMLElement[] = [];
    activeElement: HTMLElement|null = null;
    isSticky = false;
    isHidden = false;
    elBounds: DOMRect = null;
    timeoutId = null;

    mounted() {
      if (this.hideOnMobile && window.innerWidth < breakpoints.tablet) {
        this.isHidden = true;
      } else {
        this.updateElements();
        this.onScroll();
        window.addEventListener('scroll', throttle(this.onScroll, 150));
        pigeon.subscribe('onePageNavigation:callAnchor', this.scrollToAnchor);
        pigeon.subscribe('onePageNavigation:update', this.updateElements);
      }

      documentHelper.ready(() => {
        setTimeout(() => {
          this.scrollToAnchor(window.location.hash.replace('#', ''));
        }, 1000);
      });
    }

    updateElements() {
      this.elements = this.getElements();
      this.isHidden = this.elements.length === 0;
    }

    getElements(): HTMLElement[] {
      const elementSelector = '[data-nav-anchor]:not([data-nav-hidden])';
      return Array.prototype.slice.call(document.querySelectorAll(elementSelector));
    }


    getCurrentElement(): HTMLElement|null {
      let cur: HTMLElement|null = null;
      let ndx = this.elements.length;
      const elHeight = this.elBounds.height

      while (ndx--) {
        const item: HTMLElement = this.elements[ndx];
        const itemBounds: DOMRect = item.getBoundingClientRect();

        if (this.anchorMode) {
          if (itemBounds.top + this.offsetBasis - elHeight <= 0) {
            cur = item;
            break;
          }
        } else {
          const topBound: number = Math.round(itemBounds.top + this.offsetBasis - elHeight);
          const bottomBound: number = itemBounds.bottom + this.offsetBasis - elHeight;
          if (topBound <= 0 && bottomBound >= 0) {
            cur = item;
            break;
          }
        }
      }

      return cur;
    }

    onScroll() {
      if (this.isHidden || !this.$el) return;

      this.elBounds = this.$el.getBoundingClientRect() as DOMRect;
      this.isSticky = Math.floor(this.elBounds.top - this.offsetBasis) <= 0;
      this.activeElement = this.getCurrentElement();
    }

    onItemClick(event: MouseEvent, navItem: NavItem) {
      if (navItem.disabled) return false;

      const anchor = navItem.anchor;
      this.scrollToAnchor(anchor);
      if (this.modifyUrl) {
        window.history.replaceState(null, '', `#${anchor}`);
      }

      event.preventDefault();
      return false;
    }

    scrollToAnchor(anchor: string) {
      const element = this.elements.find(elm => elm.dataset['navAnchor'] === anchor);
      if (element) {
        this.scrollToItem(element);
      }
    }

    @Watch('activeElement')
    onActiveElementChange() {
      window.clearTimeout(this.timeoutId);
      this.timeoutId = window.setTimeout(() => {
        const activeNavRef = this.$refs.activeNavElement as any[];
        const activeNavElement = activeNavRef?.length ? activeNavRef[0] as HTMLElement : null;
        if (activeNavElement && this.isSticky) {
          activeNavElement.scrollIntoView({behavior: 'smooth', inline: 'center'});
        }
      }, 500);
    }

    protected scrollToItem(item: HTMLElement) {
      const offsetHeight = this.offsetBasis - this.elBounds.height + 2;
      scrollToElement(item, {
        offset: offsetHeight,
        duration: 300
      });
    }

    isNavItemActive(item: NavItem): boolean {
      if (this.activeElement) {
        return this.activeElement.dataset['navAnchor'] === item.anchor;
      }

      return false;
    }

    get navItems(): NavItem[] {
      return this.elements.map(elm => {
        const anchor = elm.dataset['navAnchor'];
        const title = elm.dataset['navTitle'] || anchor;
        const disabled = !!elm.dataset['navDisabled'];
        return {
          anchor: anchor.trim(),
          title: title.trim(),
          disabled
        }
      });
    }
  }
</script>
